import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Users, MagnifyingGlass, Eye, ShoppingCart, Cube, CurrencyDollar,
  TrendUp, ArrowsClockwise, Ticket, ShieldCheck, CaretUp, CaretDown,
  Warning, CheckCircle, Clock, ArrowsLeftRight
} from '@phosphor-icons/react';
import SortableTable from '../components/SortableTable';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n || 0);

function ScoreBadge({ score }) {
  const color = score >= 70 ? '#28cd8c' : score >= 40 ? '#f6a609' : '#ea4b5a';
  return (
    <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold" style={{ backgroundColor: `${color}15`, color }} data-testid="seller-score-badge">
      <TrendUp size={13} weight="bold" />
      {score}
    </div>
  );
}

function StatCard({ label, value, sub, icon: Icon, color = '#7d64ff', testId }) {
  return (
    <div className="bg-white border border-[#e5e5ea] rounded-xl p-4 flex items-start gap-3" data-testid={testId}>
      <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${color}12` }}>
        <Icon size={18} style={{ color }} weight="bold" />
      </div>
      <div className="min-w-0">
        <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[#9c9db7]">{label}</p>
        <p className="text-xl font-bold text-[#0A0A0A] mt-0.5">{value}</p>
        {sub && <p className="text-[11px] text-[#5a5c87] mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

function SellerDetailDialog({ seller, open, onClose }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (seller && open) {
      setLoading(true);
      axios.get(`${API_URL}/api/admin/sellers/${seller.user_id}`, { withCredentials: true })
        .then(res => setDetail(res.data))
        .catch(() => toast.error('Failed to load seller detail'))
        .finally(() => setLoading(false));
    }
  }, [seller, open]);

  if (!seller) return null;
  const d = detail || {};
  const perf = d.performance || {};
  const orders = d.orders || {};
  const rev = d.revenue || {};
  const catalog = d.catalog || {};
  const payments = d.payments || {};
  const returns = d.returns || {};

  const orderColumns = [
    { key: 'order_id', label: 'Order ID', render: v => <span className="font-mono text-xs">{v}</span> },
    { key: 'customer_name', label: 'Customer' },
    { key: 'total_amount', label: 'Amount', render: v => fmt(v) },
    { key: 'status', label: 'Status', render: v => {
      const colors = { pending: '#f6a609', confirmed: '#7d64ff', shipped: '#3b82f6', delivered: '#28cd8c', cancelled: '#ea4b5a', rejected: '#ea4b5a' };
      return <span className="px-2 py-0.5 text-[11px] font-bold rounded capitalize" style={{ backgroundColor: `${colors[v] || '#9c9db7'}15`, color: colors[v] || '#9c9db7' }}>{v}</span>;
    }},
    { key: 'created_at', label: 'Date', render: v => v ? new Date(v).toLocaleDateString() : '-' },
  ];

  const productColumns = [
    { key: 'sku', label: 'SKU', render: v => <span className="font-mono text-xs">{v}</span> },
    { key: 'name', label: 'Product' },
    { key: 'category', label: 'Category' },
    { key: 'price', label: 'Price', render: v => fmt(v) },
    { key: 'qc_status', label: 'QC', render: v => {
      const c = v === 'approved' ? '#28cd8c' : v === 'rejected' ? '#ea4b5a' : '#f6a609';
      return <span className="px-2 py-0.5 text-[11px] font-bold rounded capitalize" style={{ backgroundColor: `${c}15`, color: c }}>{v}</span>;
    }},
  ];

  const settlementColumns = [
    { key: 'settlement_id', label: 'ID', render: v => <span className="font-mono text-[11px]">{v?.slice(0, 14)}</span> },
    { key: 'period_start', label: 'Period', render: (v, row) => `${v ? new Date(v).toLocaleDateString() : '-'} - ${row.period_end ? new Date(row.period_end).toLocaleDateString() : '-'}` },
    { key: 'total_order_value', label: 'GMV', render: v => fmt(v) },
    { key: 'total_commission', label: 'Commission', render: v => <span className="text-[#ea4b5a]">-{fmt(v)}</span> },
    { key: 'net_payable', label: 'Net Payable', render: v => <span className="font-semibold text-[#28cd8c]">{fmt(v)}</span> },
    { key: 'status', label: 'Status', render: v => {
      const c = v === 'paid' ? '#28cd8c' : v === 'approved' ? '#7d64ff' : v === 'rejected' ? '#ea4b5a' : '#f6a609';
      return <span className="px-2 py-0.5 text-[11px] font-bold rounded capitalize" style={{ backgroundColor: `${c}15`, color: c }}>{v}</span>;
    }},
  ];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#7d64ff]/10 text-[#7d64ff] flex items-center justify-center text-lg font-bold">
              {(d.name || seller.name || '?')[0].toUpperCase()}
            </div>
            <div>
              <span className="text-lg">{d.name || seller.name}</span>
              <p className="text-xs text-[#5a5c87] font-normal">{d.email || seller.email}</p>
            </div>
            {!loading && detail && <ScoreBadge score={perf.score || 0} />}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="space-y-4 py-4">
            <div className="h-10 skeleton rounded-lg w-3/4" />
            <div className="grid grid-cols-2 gap-3">{[1,2,3,4].map(i => <div key={i} className="h-20 skeleton rounded-lg" />)}</div>
            <div className="h-32 skeleton rounded-lg" />
          </div>
        ) : detail ? (
          <Tabs defaultValue="overview" className="mt-4 space-y-4">
            <TabsList>
              <TabsTrigger value="overview" data-testid="detail-tab-overview">Overview</TabsTrigger>
              <TabsTrigger value="orders" data-testid="detail-tab-orders">Orders</TabsTrigger>
              <TabsTrigger value="catalog" data-testid="detail-tab-catalog">Catalog</TabsTrigger>
              <TabsTrigger value="payments" data-testid="detail-tab-payments">Payments</TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview">
              {/* Seller Info */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
                <div className="p-3 bg-[#fcfcfc] rounded-lg border border-[#f0f0f0]">
                  <p className="text-[10px] uppercase tracking-widest text-[#9c9db7] font-semibold">Phone</p>
                  <p className="text-sm font-medium mt-0.5">{d.phone || '-'}</p>
                </div>
                <div className="p-3 bg-[#fcfcfc] rounded-lg border border-[#f0f0f0]">
                  <p className="text-[10px] uppercase tracking-widest text-[#9c9db7] font-semibold">Business</p>
                  <p className="text-sm font-medium mt-0.5">{d.business_name || '-'}</p>
                </div>
                <div className="p-3 bg-[#fcfcfc] rounded-lg border border-[#f0f0f0]">
                  <p className="text-[10px] uppercase tracking-widest text-[#9c9db7] font-semibold">Type</p>
                  <p className="text-sm font-medium mt-0.5">{d.business_type || '-'}</p>
                </div>
                <div className="p-3 bg-[#fcfcfc] rounded-lg border border-[#f0f0f0]">
                  <p className="text-[10px] uppercase tracking-widest text-[#9c9db7] font-semibold">KYC Status</p>
                  <p className="text-sm font-medium mt-0.5 capitalize" style={{ color: d.kyc_status === 'approved' ? '#28cd8c' : d.kyc_status === 'rejected' ? '#ea4b5a' : '#f6a609' }}>{d.kyc_status}</p>
                </div>
              </div>

              {d.selected_categories?.length > 0 && (
                <div className="mb-5">
                  <p className="text-[10px] uppercase tracking-widest text-[#9c9db7] font-semibold mb-2">Registered Categories</p>
                  <div className="flex flex-wrap gap-1.5">
                    {d.selected_categories.map((c, i) => <span key={i} className="px-2.5 py-1 text-xs bg-[#7d64ff]/8 text-[#7d64ff] rounded font-medium">{c}</span>)}
                  </div>
                </div>
              )}

              {/* KPIs */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
                <StatCard label="Performance Score" value={perf.score || 0} sub={`${perf.fulfillment_rate}% fulfillment`} icon={TrendUp} color="#7d64ff" testId="detail-stat-score" />
                <StatCard label="Total Revenue" value={fmt(rev.total_revenue)} sub={`AOV ${fmt(rev.avg_order_value)}`} icon={CurrencyDollar} color="#28cd8c" testId="detail-stat-revenue" />
                <StatCard label="Total Orders" value={orders.total || 0} sub={`${orders.delivered} delivered`} icon={ShoppingCart} color="#3b82f6" testId="detail-stat-orders" />
                <StatCard label="Products" value={catalog.total_products || 0} sub={`${catalog.active} active`} icon={Cube} color="#f6a609" testId="detail-stat-products" />
              </div>

              {/* Performance Rates */}
              <div className="grid grid-cols-3 gap-3 mb-5">
                {[
                  { label: 'Fulfillment', value: `${perf.fulfillment_rate}%`, good: perf.fulfillment_rate >= 80, icon: CheckCircle },
                  { label: 'Cancellation', value: `${perf.cancellation_rate}%`, good: perf.cancellation_rate < 10, icon: Warning },
                  { label: 'Return Rate', value: `${perf.return_rate}%`, good: perf.return_rate < 5, icon: ArrowsClockwise },
                ].map(m => (
                  <div key={m.label} className="p-3 rounded-lg border border-[#e5e5ea] flex items-center gap-3">
                    <m.icon size={20} weight="bold" className={m.good ? 'text-[#28cd8c]' : 'text-[#ea4b5a]'} />
                    <div>
                      <p className="text-[10px] uppercase tracking-widest text-[#9c9db7] font-semibold">{m.label}</p>
                      <p className={`text-lg font-bold ${m.good ? 'text-[#28cd8c]' : 'text-[#ea4b5a]'}`}>{m.value}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Category Breakdown */}
              {catalog.category_breakdown?.length > 0 && (
                <div className="mb-4">
                  <p className="text-[10px] uppercase tracking-widest text-[#9c9db7] font-semibold mb-2">Category Breakdown</p>
                  <div className="flex flex-wrap gap-2">
                    {catalog.category_breakdown.map(c => (
                      <span key={c.category} className="px-3 py-1.5 bg-[#f7f7fa] border border-[#e5e5ea] rounded text-xs">
                        <strong>{c.category}</strong> <span className="text-[#5a5c87]">({c.count})</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Quick Info Row */}
              <div className="flex items-center gap-4 text-xs text-[#5a5c87] pt-3 border-t border-[#f0f0f0]">
                <span>Joined: {d.created_at ? new Date(d.created_at).toLocaleDateString() : '-'}</span>
                <span>Open Tickets: <strong className={d.open_tickets > 0 ? 'text-[#f6a609]' : 'text-[#28cd8c]'}>{d.open_tickets || 0}</strong></span>
                <span>Commission: <strong className="text-[#ea4b5a]">{fmt(rev.total_commission)}</strong></span>
              </div>
            </TabsContent>

            {/* Orders Tab */}
            <TabsContent value="orders">
              <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mb-4">
                {[
                  { label: 'Total', value: orders.total, color: '#5a5c87' },
                  { label: 'Pending', value: orders.pending, color: '#f6a609' },
                  { label: 'Shipped', value: orders.shipped, color: '#3b82f6' },
                  { label: 'Delivered', value: orders.delivered, color: '#28cd8c' },
                  { label: 'Cancelled', value: orders.cancelled, color: '#ea4b5a' },
                  { label: 'Returns', value: returns.total, color: '#f6a609' },
                ].map(s => (
                  <div key={s.label} className="text-center p-2 rounded-lg bg-[#fcfcfc] border border-[#f0f0f0]">
                    <p className="text-[10px] uppercase tracking-wider text-[#9c9db7]">{s.label}</p>
                    <p className="text-lg font-bold" style={{ color: s.color }}>{s.value || 0}</p>
                  </div>
                ))}
              </div>
              <SortableTable columns={orderColumns} data={d.recent_orders || []} pageSize={10} emptyMessage="No orders" emptyIcon={ShoppingCart} />
            </TabsContent>

            {/* Catalog Tab */}
            <TabsContent value="catalog">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4">
                {[
                  { label: 'Total', value: catalog.total_products, color: '#5a5c87' },
                  { label: 'Active', value: catalog.active, color: '#28cd8c' },
                  { label: 'Pending QC', value: catalog.pending_qc, color: '#f6a609' },
                  { label: 'Approved', value: catalog.approved, color: '#7d64ff' },
                  { label: 'Rejected', value: catalog.rejected, color: '#ea4b5a' },
                ].map(s => (
                  <div key={s.label} className="text-center p-2 rounded-lg bg-[#fcfcfc] border border-[#f0f0f0]">
                    <p className="text-[10px] uppercase tracking-wider text-[#9c9db7]">{s.label}</p>
                    <p className="text-lg font-bold" style={{ color: s.color }}>{s.value || 0}</p>
                  </div>
                ))}
              </div>
              <SortableTable columns={productColumns} data={d.recent_products || []} pageSize={10} emptyMessage="No products" emptyIcon={Cube} />
            </TabsContent>

            {/* Payments Tab */}
            <TabsContent value="payments">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <StatCard label="Total Revenue" value={fmt(rev.total_revenue)} icon={CurrencyDollar} color="#28cd8c" testId="payment-stat-revenue" />
                <StatCard label="Commission Deducted" value={fmt(rev.total_commission)} icon={CurrencyDollar} color="#ea4b5a" testId="payment-stat-commission" />
                <StatCard label="Total Settled" value={fmt(payments.total_settled)} icon={CheckCircle} color="#7d64ff" testId="payment-stat-settled" />
                <StatCard label="Pending Settlement" value={fmt(payments.pending_settlement)} icon={Clock} color="#f6a609" testId="payment-stat-pending" />
              </div>
              <SortableTable columns={settlementColumns} data={payments.settlements || []} pageSize={10} emptyMessage="No settlements yet" emptyIcon={CurrencyDollar} />
            </TabsContent>
          </Tabs>
        ) : (
          <div className="py-16 text-center text-[#9c9db7]">No data available</div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function AdminSellersPage() {
  const navigate = useNavigate();
  const [sellers, setSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [kycFilter, setKycFilter] = useState('');
  const [sortBy, setSortBy] = useState('revenue');
  const [selectedSeller, setSelectedSeller] = useState(null);
  const [compareIds, setCompareIds] = useState([]);

  useEffect(() => { fetchSellers(); }, []);

  const fetchSellers = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/sellers`, { withCredentials: true });
      // API returns {sellers: [...], total, page, pages} - extract sellers array
      setSellers(res.data.sellers || res.data || []);
    } catch (e) { toast.error('Failed to load sellers'); }
    finally { setLoading(false); }
  };

  const filtered = sellers.filter(s => {
    const matchSearch = !search || s.name?.toLowerCase().includes(search.toLowerCase()) || s.email?.toLowerCase().includes(search.toLowerCase()) || s.business_name?.toLowerCase().includes(search.toLowerCase());
    const matchKyc = !kycFilter || s.kyc_status === kycFilter;
    return matchSearch && matchKyc;
  }).sort((a, b) => {
    if (sortBy === 'revenue') return (b.stats?.total_revenue || 0) - (a.stats?.total_revenue || 0);
    if (sortBy === 'orders') return (b.stats?.total_orders || 0) - (a.stats?.total_orders || 0);
    if (sortBy === 'products') return (b.stats?.total_products || 0) - (a.stats?.total_products || 0);
    if (sortBy === 'newest') return new Date(b.created_at) - new Date(a.created_at);
    return 0;
  });

  // Aggregate stats
  const totalSellers = sellers.length;
  const activeSellers = sellers.filter(s => s.is_active).length;
  const kycApproved = sellers.filter(s => s.kyc_status === 'approved').length;
  const totalGmv = sellers.reduce((sum, s) => sum + (s.stats?.total_revenue || 0), 0);

  const toggleCompare = (userId) => {
    setCompareIds(prev => {
      if (prev.includes(userId)) return prev.filter(id => id !== userId);
      if (prev.length >= 4) { toast.error('Max 4 sellers for comparison'); return prev; }
      return [...prev, userId];
    });
  };

  const columns = [
    { key: 'compare', label: '', sortable: false, render: (v, row) => (
      <input type="checkbox" checked={compareIds.includes(row.user_id)} onChange={() => toggleCompare(row.user_id)}
        className="w-4 h-4 rounded border-[#e5e5ea] text-[#7d64ff] focus:ring-[#7d64ff] cursor-pointer accent-[#7d64ff]"
        data-testid={`compare-check-${row.user_id}`} />
    )},
    { key: 'name', label: 'Seller', render: (v, row) => (
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-full bg-[#7d64ff]/10 text-[#7d64ff] flex items-center justify-center text-sm font-bold flex-shrink-0">
          {(v || '?')[0].toUpperCase()}
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-[#0A0A0A] truncate">{v}</p>
          <p className="text-[11px] text-[#9c9db7] truncate">{row.email}</p>
        </div>
      </div>
    )},
    { key: 'business_name', label: 'Business', render: (v, row) => (
      <div>
        <p className="text-sm">{v || '-'}</p>
        <p className="text-[11px] text-[#9c9db7]">{row.business_type || ''}</p>
      </div>
    )},
    { key: 'kyc_status', label: 'KYC', render: v => {
      const c = v === 'approved' ? '#28cd8c' : v === 'rejected' ? '#ea4b5a' : '#f6a609';
      return <span className="px-2 py-0.5 text-[11px] font-bold rounded capitalize" style={{ backgroundColor: `${c}15`, color: c }}>{v}</span>;
    }},
    { key: 'stats_orders', label: 'Orders', render: (v, row) => (
      <div className="text-center">
        <p className="font-semibold">{row.stats?.total_orders || 0}</p>
        <p className="text-[10px] text-[#9c9db7]">{row.stats?.delivered_orders || 0} delivered</p>
      </div>
    )},
    { key: 'stats_revenue', label: 'Revenue', render: (v, row) => (
      <span className="font-semibold text-[#28cd8c]">{fmt(row.stats?.total_revenue)}</span>
    )},
    { key: 'stats_products', label: 'Products', render: (v, row) => (
      <div className="text-center">
        <p className="font-semibold">{row.stats?.total_products || 0}</p>
        <p className="text-[10px] text-[#9c9db7]">{row.stats?.active_products || 0} active</p>
      </div>
    )},
    { key: 'stats_fulfillment', label: 'Fulfillment', render: (v, row) => {
      const rate = row.stats?.fulfillment_rate || 0;
      const c = rate >= 80 ? '#28cd8c' : rate >= 50 ? '#f6a609' : '#ea4b5a';
      return <span className="font-semibold" style={{ color: c }}>{rate}%</span>;
    }},
    { key: 'stats_settlement', label: 'Settlement', render: (v, row) => (
      <div>
        <p className="text-xs font-medium">{fmt(row.stats?.total_settled)}</p>
        {(row.stats?.pending_settlement || 0) > 0 && <p className="text-[10px] text-[#f6a609]">{fmt(row.stats?.pending_settlement)} pending</p>}
      </div>
    )},
    { key: 'user_id', label: '', sortable: false, render: (v, row) => (
      <Button variant="ghost" size="sm" className="text-[#7d64ff]" onClick={() => setSelectedSeller(row)} data-testid={`view-seller-${v}`}>
        <Eye size={16} className="mr-1" /> View
      </Button>
    )},
  ];

  return (
    <DashboardLayout title="Seller Management" subtitle="View and manage all marketplace sellers">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatCard label="Total Sellers" value={totalSellers} icon={Users} color="#7d64ff" testId="stat-total-sellers" />
        <StatCard label="Active Sellers" value={activeSellers} sub={`${kycApproved} KYC approved`} icon={CheckCircle} color="#28cd8c" testId="stat-active-sellers" />
        <StatCard label="Total GMV" value={fmt(totalGmv)} icon={CurrencyDollar} color="#3b82f6" testId="stat-total-gmv" />
        <StatCard label="Avg Revenue/Seller" value={fmt(totalGmv / Math.max(totalSellers, 1))} icon={TrendUp} color="#f6a609" testId="stat-avg-revenue" />
      </div>

      {/* Filters */}
      <div className="bg-white border border-[#e5e5ea] mb-6">
        <div className="action-bar">
          <div className="relative flex-1 max-w-md">
            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5a5c87]" size={18} />
            <Input placeholder="Search by name, email, or business..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10 rounded-sm" data-testid="seller-search" />
          </div>
          <Select value={kycFilter || "all"} onValueChange={v => setKycFilter(v === "all" ? "" : v)}>
            <SelectTrigger className="w-36 rounded-sm" data-testid="kyc-filter"><SelectValue placeholder="KYC Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All KYC</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-40 rounded-sm" data-testid="sort-filter"><SelectValue placeholder="Sort By" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="revenue">Top Revenue</SelectItem>
              <SelectItem value="orders">Most Orders</SelectItem>
              <SelectItem value="products">Most Products</SelectItem>
              <SelectItem value="newest">Newest First</SelectItem>
            </SelectContent>
          </Select>
          {compareIds.length >= 2 && (
            <Button
              onClick={() => navigate(`/admin/seller-comparison?ids=${compareIds.join(',')}`)}
              className="bg-[#7d64ff] hover:bg-[#6b54e6] text-white text-xs gap-1.5"
              data-testid="compare-sellers-btn"
            >
              <ArrowsLeftRight size={14} weight="bold" />
              Compare {compareIds.length} Sellers
            </Button>
          )}
          {compareIds.length === 1 && (
            <span className="text-[11px] text-[#9c9db7] self-center">Select 1 more to compare</span>
          )}
        </div>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1,2,3,4,5,6,7,8].map(i => <div key={i} className="h-14 skeleton rounded-xl" />)}
        </div>
      ) : (
        <SortableTable columns={columns} data={filtered} pageSize={15} emptyMessage="No sellers found" emptyIcon={Users} />
      )}

      <SellerDetailDialog seller={selectedSeller} open={!!selectedSeller} onClose={() => setSelectedSeller(null)} />
    </DashboardLayout>
  );
}
